/**
 * Market Configuration Module
 * 
 * Centralized configuration for all markets (India, UAE, Saudi Arabia, UK, Singapore, USA, Europe)
 * Provides market detection, currency conversion, and catalog prefixing
 */
window.MarketConfig = {
  // Primary market (India) - Razorpay
  default: {
    market: 'INR',
    currency: 'INR',
    gateway: 'razorpay',
    catalogPrefix: '',
    locale: 'en',
    path: '/',
    countryCode: 'IN',
    phoneCountryCode: '+91',
    currencySymbol: '₹',
    conversionRate: 1, // No conversion needed
    stripeEnabled: false,
    razorpayMagicCheckoutEnabled: true // Magic Checkout enabled for India
  },
  
  // International markets - Stripe
  markets: {
    'en-ae': {
      market: 'UAE',
      currency: 'AED',
      gateway: 'stripe',
      catalogPrefix: 'uae_',
      locale: 'en-ae',
      path: '/en-ae/',
      countryCode: 'AE',
      phoneCountryCode: '+971',
      currencySymbol: 'AED',
      conversionRate: 0.04, // INR to AED (update with actual rate)
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false // Magic Checkout disabled for UAE
    },
    'ar-ae': {
      market: 'UAE',
      currency: 'AED',
      gateway: 'stripe',
      catalogPrefix: 'uae_',
      locale: 'ar-ae',
      path: '/ar-ae/',
      countryCode: 'AE',
      phoneCountryCode: '+971',
      currencySymbol: 'AED',
      conversionRate: 0.04,
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    },
    'en-sa': {
      market: 'Saudi Arabia',
      currency: 'SAR',
      gateway: 'stripe',
      catalogPrefix: 'uae_', // Use UAE catalog for all international markets
      locale: 'en-sa',
      path: '/en-sa/',
      countryCode: 'SA',
      phoneCountryCode: '+966',
      currencySymbol: 'SAR',
      conversionRate: 0.04, // INR to SAR (update with actual rate)
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    },
    'ar-sa': {
      market: 'Saudi Arabia',
      currency: 'SAR',
      gateway: 'stripe',
      catalogPrefix: 'uae_',
      locale: 'ar-sa',
      path: '/ar-sa/',
      countryCode: 'SA',
      phoneCountryCode: '+966',
      currencySymbol: 'SAR',
      conversionRate: 0.04,
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    },
    'en-gb': {
      market: 'UK',
      currency: 'GBP',
      gateway: 'stripe',
      catalogPrefix: 'uae_', // Use UAE catalog for all international markets
      locale: 'en-gb',
      path: '/en-gb/',
      countryCode: 'GB',
      phoneCountryCode: '+44',
      currencySymbol: '£',
      conversionRate: 0.0083, // INR to GBP (update with actual rate)
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    },
    'en-sg': {
      market: 'Singapore',
      currency: 'SGD',
      gateway: 'stripe',
      catalogPrefix: 'uae_', // Use UAE catalog for all international markets
      locale: 'en-sg',
      path: '/en-sg/',
      countryCode: 'SG',
      phoneCountryCode: '+65',
      currencySymbol: 'S$',
      conversionRate: 0.016, // INR to SGD (update with actual rate)
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    },
    'en-us': {
      market: 'USA',
      currency: 'USD',
      gateway: 'stripe',
      catalogPrefix: 'uae_', // Use UAE catalog for all international markets
      locale: 'en-us',
      path: '/en-us/',
      countryCode: 'US',
      phoneCountryCode: '+1',
      currencySymbol: '$',
      conversionRate: 0.0111, // INR to USD (update with actual rate)
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    },
    'en-eu': {
      market: 'Europe',
      currency: 'EUR',
      gateway: 'stripe',
      catalogPrefix: 'uae_', // Use UAE catalog for all international markets
      locale: 'en-eu',
      path: '/en-eu/',
      countryCode: 'EU', // Will need country-specific handling
      phoneCountryCode: '+33', // Default to France
      currencySymbol: '€',
      conversionRate: 0.0095, // INR to EUR (update with actual rate)
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    },
    'en-ca': {
      market: 'Canada',
      currency: 'CAD',
      gateway: 'stripe',
      catalogPrefix: 'uae_', // Use UAE catalog for all international markets
      locale: 'en-ca',
      path: '/en-ca/',
      countryCode: 'CA',
      phoneCountryCode: '+1',
      currencySymbol: 'CA$',
      conversionRate: 0.012, // INR to CAD (placeholder, update with actual rate)
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    },
    'en-au': {
      market: 'Australia',
      currency: 'AUD',
      gateway: 'stripe',
      catalogPrefix: 'uae_', // Use UAE catalog for all international markets
      locale: 'en-au',
      path: '/en-au/',
      countryCode: 'AU',
      phoneCountryCode: '+61',
      currencySymbol: 'A$',
      conversionRate: 0.018, // INR to AUD (placeholder, update with actual rate)
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    }
  },
  
  /**
   * Detect current market from URL, hostname, or locale
   * @returns {Object} { key: string, config: Object }
   */
  detectMarket: function() {
    const pathname = window.location.pathname;
    const hostname = window.location.hostname;

    // Market subfolders are always {language}-{market} (/en-ae/, /ar-sa/,
    // /de-eu/). Resolve on the market suffix so a new language on an existing
    // market needs no entry in `markets` above. An exact key wins first, so
    // every locale that does have its own entry behaves exactly as before.
    const firstSegment = (pathname.toLowerCase().replace(/^\//, '').split('/')[0] || '');
    if (/^[a-z]{2}-[a-z]{2}$/.test(firstSegment)) {
      if (this.markets[firstSegment]) {
        return { key: firstSegment, config: this.markets[firstSegment] };
      }
      const suffix = firstSegment.slice(3);
      for (const [key, config] of Object.entries(this.markets)) {
        if (key.slice(3) === suffix) {
          // Same market, different language: report the locale actually in the
          // URL rather than the entry's own key. Cloned so the shared entry
          // (and the rate written into it by theme.liquid) is left intact.
          return {
            key: firstSegment,
            config: Object.assign({}, config, { locale: firstSegment, path: '/' + firstSegment + '/' }),
          };
        }
      }
    }

    // Check path-based detection (handle both with and without trailing slash)
    for (const [key, config] of Object.entries(this.markets)) {
      // Check if pathname starts with the market path (with or without trailing slash)
      const pathPattern = new RegExp('^' + config.path.replace(/\/$/, '') + '(?:/|$)');
      if (pathPattern.test(pathname) || pathname.includes(config.path) || hostname.includes(key)) {
        return { key, config };
      }
    }
    
    // Check window.shopifyLocale if available
    if (window.shopifyLocale && window.shopifyLocale.code) {
      const locale = window.shopifyLocale.code;
      if (this.markets[locale]) {
        return { key: locale, config: this.markets[locale] };
      }
    }
    
    // Default to India
    return { key: 'default', config: this.default };
  },
  
  /**
   * Get market config for a specific locale
   * @param {string} locale - Locale code (e.g., 'en-ae', 'en-sa')
   * @returns {Object} Market configuration
   */
  getMarketConfig: function(locale) {
    return this.markets[locale] || this.default;
  },
  
  /**
   * Check if current market is international (not India)
   * @returns {boolean}
   */
  isInternationalMarket: function() {
    const marketInfo = this.detectMarket();
    return marketInfo.key !== 'default';
  },
  
  /**
   * Convert price from INR (cents) to market currency
   * @param {number} priceInCents - Price in INR cents (Shopify format)
   * @param {string} marketKey - Optional market key, if not provided uses detected market
   * @returns {number} Price in market currency
   */
  convertPrice: function(priceInCents, marketKey) {
    const marketInfo = marketKey ? { config: this.markets[marketKey] || this.default } : this.detectMarket();
    const config = marketInfo.config;
    
    if (!config || config.conversionRate === 1) {
      return priceInCents / 100; // Return in base currency (INR)
    }
    
    const inrAmount = priceInCents / 100;
    return inrAmount * config.conversionRate;
  },
  
  /**
   * Prefix content IDs for Meta catalog matching
   * @param {Array|string} ids - Content IDs to prefix
   * @param {string} marketKey - Optional market key, if not provided uses detected market
   * @returns {Array} Prefixed content IDs
   */
  prefixContentIds: function(ids, marketKey) {
    const marketInfo = marketKey ? { config: this.markets[marketKey] || this.default } : this.detectMarket();
    const config = marketInfo.config;
    
    if (!config || !config.catalogPrefix) {
      // Return as array if input was array, otherwise return as-is
      return Array.isArray(ids) ? ids : [ids];
    }
    
    const prefix = config.catalogPrefix;
    if (!Array.isArray(ids)) {
      ids = [ids];
    }
    
    return ids.map(id => {
      if (!id) return null;
      const idStr = id.toString();
      // If already has prefix, return as-is
      if (idStr.startsWith(prefix)) {
        return idStr;
      }
      // Add prefix
      return prefix + idStr;
    }).filter(id => id !== null);
  },
  
  /**
   * Format a market-currency amount for DISPLAY, in the convention of the
   * current locale. This is the single JS source of truth for how a price
   * looks; it mirrors snippets/market-price.liquid — change both together.
   *
   *   de/fr/it/es on the EU market:  "1.234,00 €"   (nbsp before the symbol)
   *   nl on the EU market:           "€ 1.234,00"
   *   everything else:               "AED 1,234.00" / "$1,234.00" / "Rs. 1,234.00"
   *   (letter-ending codes keep one space; sign symbols hug the number)
   *
   * @param {number} amount - Amount in market currency UNITS (not cents)
   * @param {Object} opts - { trimWholeDecimals: true } drops ".00" on whole
   *   amounts (used for promo copy like "AED 100"); default keeps 2 decimals.
   * @returns {string}
   */
  formatDisplay: function(amount, opts) {
    opts = opts || {};
    var info = this.getCurrentMarket();
    var config = info.config || this.default;
    var key = String(info.key || '');
    var n = Number(amount);
    if (!isFinite(n)) n = 0;
    var showDecimals = !(opts.trimWholeDecimals && n % 1 === 0);
    var parts = n.toFixed(showDecimals ? 2 : 0).split('.');

    var lang = key.slice(0, 2);
    var euStyle = key.slice(3) === 'eu' && ['de', 'fr', 'it', 'es', 'nl'].indexOf(lang) !== -1;
    if (euStyle) {
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      var euNum = parts[1] ? parts[0] + ',' + parts[1] : parts[0];
      // Dutch puts the symbol first; French/German/Italian/Spanish put it last.
      //   (nbsp) keeps the symbol from wrapping away from its number.
      return lang === 'nl' ? config.currencySymbol + '\u00A0' + euNum : euNum + '\u00A0' + config.currencySymbol;
    }

    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var num = parts[1] ? parts[0] + '.' + parts[1] : parts[0];
    if (key === 'default') {
      return 'Rs. ' + num; // match the shop money format exactly
    }
    var symbol = config.currencySymbol || '$';
    return /[A-Za-z]$/.test(symbol) ? symbol + ' ' + num : symbol + num;
  },

  /**
   * Parse a price string rendered by formatDisplay / market-price.liquid back
   * into a Number of market units. The inverse of formatDisplay for the
   * current locale; returns null when no number is present.
   * @param {string} text
   * @returns {number|null}
   */
  parseDisplay: function(text) {
    var m = String(text || '').replace(/\u00A0/g, ' ').match(/(\d[\d.,]*)/);
    if (!m) return null;
    var num = m[1];
    var key = String(this.getCurrentMarket().key || '');
    var lang = key.slice(0, 2);
    var euStyle = key.slice(3) === 'eu' && ['de', 'fr', 'it', 'es', 'nl'].indexOf(lang) !== -1;
    var value = euStyle
      ? parseFloat(num.replace(/\./g, '').replace(',', '.')) // "1.234,56" -> 1234.56
      : parseFloat(num.replace(/,/g, ''));                   // "1,234.56" -> 1234.56
    return isFinite(value) ? value : null;
  },

  /**
   * Get current market info (cached after first call)
   * @returns {Object} { key: string, config: Object }
   */
  getCurrentMarket: function() {
    if (!this._currentMarket) {
      this._currentMarket = this.detectMarket();
    }
    return this._currentMarket;
  },
  
  /**
   * Reset cached market (useful for testing or when locale changes)
   */
  resetMarketCache: function() {
    this._currentMarket = null;
  }
};

// Initialize current market on load
if (typeof window !== 'undefined') {
  // Store detected market for quick access
  window.currentMarket = window.MarketConfig ? window.MarketConfig.getCurrentMarket() : null;
  // Removed console.log for production performance
}

